import { useEffect, useState } from "react";
import { Upload, Download, Trash2, Archive, FolderOpen, FileText, Pencil } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, Modal, Field, ErrBox, Empty } from "../../components/common";

export default function Documents() {
  const [data, setData] = useState(null);
  const [folder, setFolder] = useState("");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upForm, setUpForm] = useState({ folder: "Other", tags: "", notes: "", file: null });
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ filename: "", folder: "Other", tags: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const q = new URLSearchParams({ folder, search });
    API.get(`/documents?${q}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [folder, search]);

  const upload = async (e) => {
    e.preventDefault();
    if (!upForm.file) { setErr("Choose a file first."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", upForm.file);
      fd.append("folder", upForm.folder);
      fd.append("tags", upForm.tags);
      fd.append("notes", upForm.notes);
      await API.post("/documents/upload", fd);
      toast.success("Document stored in your vault");
      setUploadOpen(false); setUpForm({ folder: "Other", tags: "", notes: "", file: null });
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await API.patch(`/documents/${editDoc.id}`, {
        filename: editForm.filename, folder: editForm.folder,
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Document updated");
      setEditDoc(null); load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This is audit-logged.`)) return;
    try {
      await API.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const archive = async (doc) => {
    await API.patch(`/documents/${doc.id}`, { archived: true });
    toast.success("Archived");
    load();
  };

  const download = (doc) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`, "_blank");
  };

  if (!data) return <Loading label="Loading document vault" />;

  return (
    <div data-testid="documents-page">
      <PageHeader title="Document Vault" sub="Audit-ready storage for every tax record."
        right={<button className="btn-navy" onClick={() => { setUploadOpen(true); setErr(""); }} data-testid="upload-doc-btn"><Upload className="h-4 w-4" /> Upload Document</button>} />

      <div className="grid gap-6 lg:grid-cols-4">
        <aside className="lg:col-span-1">
          <div className="card-gov p-2">
            <button onClick={() => setFolder("")} data-testid="folder-all"
              className={`flex w-full items-center justify-between rounded px-3 py-2 text-sm font-semibold ${!folder ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <span className="flex items-center gap-2"><FolderOpen className="h-4 w-4" /> All Documents</span>
            </button>
            {data.folders.map((f) => (
              <button key={f} onClick={() => setFolder(f)} data-testid={`folder-${f.toLowerCase().replace(/ /g, "-")}`}
                className={`flex w-full items-center justify-between rounded px-3 py-2 text-sm ${folder === f ? "bg-navy text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}>
                <span>{f}</span>
                <span className="text-xs text-slate-400">{data.folder_counts[f] || 0}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-3">
          <input className="input-gov mb-4 max-w-sm" placeholder="Search documents…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="doc-search" />
          {data.documents.length === 0 ? (
            <Empty title="No documents" msg="Upload returns, notices, statements, or confirmations." />
          ) : (
            <div className="space-y-2">
              {data.documents.map((d) => (
                <div key={d.id} className="card-gov flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`doc-${d.filename}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-navy" />
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{d.filename}</div>
                      <div className="text-xs text-slate-400">
                        {d.folder} · {(d.size / 1024).toFixed(1)} KB · {d.uploaded_by} · {fmtDate(d.created_at)}
                        {d.tags?.length > 0 && <> · {d.tags.map((t) => <span key={t} className="mr-1 rounded bg-slate-100 px-1">{t}</span>)}</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => download(d)} className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Download" data-testid={`download-${d.id}`}><Download className="h-4 w-4" /></button>
                    <button onClick={() => { setEditDoc(d); setEditForm({ filename: d.filename, folder: d.folder, tags: (d.tags || []).join(", ") }); setErr(""); }} className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Rename / move" data-testid={`edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => archive(d)} className="rounded p-2 text-slate-500 hover:bg-slate-100" title="Archive" data-testid={`archive-${d.id}`}><Archive className="h-4 w-4" /></button>
                    <button onClick={() => remove(d)} className="rounded p-2 text-rose-500 hover:bg-rose-50" title="Delete" data-testid={`delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload to Document Vault">
        <form onSubmit={upload} className="space-y-4" data-testid="upload-form">
          <Field label="File *"><input type="file" onChange={(e) => setUpForm({ ...upForm, file: e.target.files[0] })} data-testid="upload-file" /></Field>
          <Field label="Folder">
            <select className="input-gov" value={upForm.folder} onChange={(e) => setUpForm({ ...upForm, folder: e.target.value })} data-testid="upload-folder">
              {data.folders.map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Tags (comma separated)"><input className="input-gov" value={upForm.tags} onChange={(e) => setUpForm({ ...upForm, tags: e.target.value })} data-testid="upload-tags" /></Field>
          <Field label="Notes"><input className="input-gov" value={upForm.notes} onChange={(e) => setUpForm({ ...upForm, notes: e.target.value })} data-testid="upload-notes" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="upload-submit">{busy ? "Uploading…" : "Upload Securely"}</button>
        </form>
      </Modal>

      <Modal open={!!editDoc} onClose={() => setEditDoc(null)} title="Edit Document">
        <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-doc-form">
          <Field label="Filename"><input className="input-gov" value={editForm.filename} onChange={(e) => setEditForm({ ...editForm, filename: e.target.value })} required data-testid="edit-filename" /></Field>
          <Field label="Folder">
            <select className="input-gov" value={editForm.folder} onChange={(e) => setEditForm({ ...editForm, folder: e.target.value })} data-testid="edit-folder">
              {data.folders.map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Tags"><input className="input-gov" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} data-testid="edit-tags" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="edit-doc-submit">{busy ? "Saving…" : "Save Changes"}</button>
        </form>
      </Modal>
    </div>
  );
}
